import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { db } from './firebase';
import { collection, getDocs } from 'firebase/firestore';

const SLUG_TO_CATEGORY = {
  'western-wear': 'Western Wear',
  'ethnic-wear': 'Ethnic Wear',
  'formal-wear': 'Formal Wear',
  'co-ord-sets': 'Co-ord Sets',
};

const PLUS_SIZES = ['4XL', '5XL', '6XL'];

export default function CategoryPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPlus = searchParams.get('plus') === '1';
  const categoryName = SLUG_TO_CATEGORY[slug];

  const [products, setProducts] = useState([]);
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  const [tryBag] = useState(() => {
    try {
      const saved = localStorage.getItem('fivetryBag');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => { if (!categoryName) navigate('/'); }, [categoryName, navigate]);

  useEffect(() => {
    if (!categoryName) return;
    const key = slug + (isPlus ? '-plus' : '');
    try {
      const seen = JSON.parse(sessionStorage.getItem('fivetryOffersSeen') || '[]');
      if (!seen.includes(key)) {
        setShowOfferPopup(true);
        seen.push(key);
        sessionStorage.setItem('fivetryOffersSeen', JSON.stringify(seen));
      }
    } catch (e) {
      setShowOfferPopup(true);
    }
  }, [categoryName, slug, isPlus]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'products'));
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = products.filter((p) => {
    if (p.category !== categoryName) return false;
    if (isPlus) return Array.isArray(p.sizes) && p.sizes.some((s) => PLUS_SIZES.includes(s));
    return true;
  });
  const isMobile = window.innerWidth < 600;

  if (!categoryName) return null;

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Arial', background: '#fafaf7', paddingBottom: '90px' }}>

      {/* NAVBAR */}
      <div style={{ position: 'sticky', top: 0, zIndex: 1000, background: '#fafaf7', borderBottom: '0.5px solid #eeeae3', display: 'flex', alignItems: 'center', padding: '18px 24px' }}>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <span onClick={() => navigate(-1)} style={{ cursor: 'pointer', fontSize: '20px', color: '#1a1a1a', padding: '4px 8px' }}>←</span>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <h1 onClick={() => navigate('/')} style={{ margin: 0, fontSize: '22px', fontWeight: '500', color: '#1a1a1a', fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '6px', paddingLeft: '6px', cursor: 'pointer' }}>FIVETRY</h1>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <span onClick={() => navigate('/?openTrybag=true')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'relative', gap: '6px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.15' }}>
              <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '2px', color: '#1a1a1a' }}>TRY</span>
              <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '2px', color: '#1a1a1a' }}>BAG</span>
            </div>
            <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2 L3 6 V20 a2 2 0 0 0 2 2 H19 a2 2 0 0 0 2-2 V6 L18 2 Z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10 a4 4 0 0 1 -8 0" />
              </svg>
              {tryBag.length > 0 && (
                <span style={{ position: 'absolute', top: '-7px', right: '-9px', background: '#1a1a1a', color: 'white', borderRadius: '50%', width: '16px', height: '16px', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600' }}>{tryBag.length}</span>
              )}
            </span>
          </span>
        </div>
      </div>

      {/* HEADER */}
      <div style={{ textAlign: 'center', padding: '30px 24px 22px' }}>
        <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '13px', letterSpacing: '5px', color: '#9c7d4f', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>{isPlus ? categoryName + ' · Plus' : categoryName}</div>
        <h2 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: isMobile ? '24px' : '28px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{isPlus ? 'Plus size collection' : 'Shop the collection'}</h2>
      </div>

      {/* PRODUCTS — 2 columns, tap to view details */}
      <div style={{ padding: '4px 16px 40px', background: '#fafaf7' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '12px' : '20px', maxWidth: '720px', margin: '0 auto' }}>
          {filteredProducts.map((product) => {
            const priceNum = parseFloat(String(product.price).replace(/[₹,]/g, ''));
            const mrpNum = parseFloat(String(product.mrp || '').replace(/[₹,]/g, ''));
            const discount = mrpNum > priceNum ? Math.round(((mrpNum - priceNum) / mrpNum) * 100) : 0;
            return (
              <div key={product.id} onClick={() => navigate('/product/' + product.id)} style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', cursor: 'pointer' }}>
                <img src={product.image} alt={product.name} style={{ width: '100%', height: isMobile ? '220px' : '300px', objectFit: 'cover' }} />
                <div style={{ padding: isMobile ? '14px' : '18px' }}>
                  <h3 style={{ margin: '0 0 6px', fontSize: '14px', color: '#111', lineHeight: '1.3' }}>{product.name}</h3>
                  {product.description && (
                    <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#777', lineHeight: '1.45', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{product.description}</p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#111', fontSize: '16px', fontWeight: '700' }}>{product.price}</span>
                    {discount > 0 && (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '13px' }}>{product.mrp}</span>
                        <span style={{ color: '#16a34a', fontSize: '12px', fontWeight: '700' }}>{discount}% OFF</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {filteredProducts.length === 0 && (
            <p style={{ color: '#666', gridColumn: '1 / -1', textAlign: 'center', padding: '40px', fontStyle: 'italic' }}>
              No styles in this category yet. Check back soon!
            </p>
          )}
        </div>
      </div>

      {showOfferPopup && (
        <div onClick={() => setShowOfferPopup(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '340px', padding: '26px 24px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '34px', marginBottom: '8px' }}>🎁</div>
            <div style={{ fontSize: '11px', letterSpacing: '2px', color: '#9c7d4f', fontWeight: '700', marginBottom: '6px' }}>THE MORE YOU KEEP, THE MORE YOU SAVE</div>
            <h2 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '22px', color: '#1a1a1a', margin: '0 0 16px' }}>Unlock bigger discounts</h2>
            <div style={{ textAlign: 'left', marginBottom: '18px' }}>
              {[{ n: 'Buy 1 item', d: '5% OFF' }, { n: 'Buy 2 items', d: '10% OFF' }, { n: 'Buy 3+ items', d: '20% OFF' }].map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', marginBottom: '8px', borderRadius: '10px', background: '#faf7f1', border: '1px solid #eeeae3' }}>
                  <span style={{ fontSize: '14px', color: '#1a1a1a', fontWeight: '600' }}>{t.n}</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#16a34a' }}>{t.d}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowOfferPopup(false)} style={{ width: '100%', background: '#1a1a1a', color: 'white', border: 'none', padding: '14px', borderRadius: '12px', fontSize: '13px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer' }}>GOT IT · LET'S SHOP</button>
          </div>
        </div>
      )}
    </div>
  );
}